import React, { FC, useEffect, useMemo } from 'react';

import { CssBaseline, PaletteMode, Theme, ThemeProvider } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import FullscreenPreloader from '~/components/atoms/FullscreenPreloader';
import { ModalContextProvider } from '~/contexts/modal/ModalContext';
import { PopoverContextProvider } from '~/contexts/popover/PopoverContext';
import { SnackBarContextProvider } from '~/features/snackbar/SnackbarContext';
import { setCssVariables } from '~/helpers/cssVariables';
import i18n from '~/i18n/config';
import { CheckAuthWrapper } from '~/middlewares/CheckAuthWrapper';
import ProfileStatusWrapper from '~/middlewares/ProfileStatusWrapper';
import { SubRebillWrapper } from '~/middlewares/SubRebillWrapper';
import { FeatureAccessInitializer } from '~/modules/feature-access/FeatureAccessInitializer';
import { HighlightContextProvider } from '~/modules/highlight/HighlightContext';
import Router from '~/Router';
import GrowthBookProvider from '~/services/abTests/Provider';
import { store } from '~/store';

import SurveyManager from './components/organisms/SurveyManager/SurveyManager';

const themeMode = 'light';

type Props = {
  createCustomTheme: (mode: PaletteMode) => Theme;
};

const App: FC<Props> = ({ createCustomTheme }) => {
  const theme = useMemo(() => createCustomTheme(themeMode), [themeMode]);
  useEffect(() => {
    setCssVariables(theme.palette, [
      'common',
      'neutral',
      'primary',
      'secondary',
      'error',
      'warning',
      'surface',
      'info',
      'success',
      'custom',
    ]);
  }, [theme]);

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n.instance}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Provider store={store}>
            <BrowserRouter>
              <SnackBarContextProvider>
                <ModalContextProvider>
                  <PopoverContextProvider>
                    <HighlightContextProvider>
                      <CheckAuthWrapper>
                        <GrowthBookProvider>
                          <ProfileStatusWrapper>
                            <SubRebillWrapper>
                              <React.Suspense
                                fallback={<FullscreenPreloader />}
                              >
                                <FeatureAccessInitializer>
                                  <Router />
                                </FeatureAccessInitializer>
                              </React.Suspense>
                            </SubRebillWrapper>
                            <SurveyManager />
                          </ProfileStatusWrapper>
                        </GrowthBookProvider>
                      </CheckAuthWrapper>
                    </HighlightContextProvider>
                  </PopoverContextProvider>
                </ModalContextProvider>
              </SnackBarContextProvider>
            </BrowserRouter>
          </Provider>
        </ThemeProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
};

export default App;
